import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { AlertTriangle, Users, ClipboardCheck, AlertCircle, RefreshCw, Calendar } from 'lucide-react';
import { useWtfLiveData } from '../hooks/useWtfLiveData';
import { WtfDataColumn, WtfSyncBadge } from '../components/wtf';

export default function ManagerView() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyMetrics, setKeyMetrics] = useState({ visit_scheduled: 0, visit_done: 0, closure_expected: 0, closure_done: 0 });
  const [teamGyms, setTeamGyms] = useState([]);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // WTF Live Data hooks
  // lead_stats: OBJECT {all_leads:"54337", todays_leads:"30", converted_leads:"7750", total_walkins:"71"}
  // targets: ARRAY [{gym_id, gym_name, daily_target, daily_achieved, remaining_target}]
  const { data: wtfLeadStats, lastSync: wtfSync, stale: wtfStale, loading: wtfLoading } = useWtfLiveData('lead_stats');
  const { data: wtfTargets } = useWtfLiveData('targets');

  // Fetch team gym list for scoping WTF data
  useEffect(() => {
    const fetchGyms = async () => {
      try {
        const res = await axiosInstance.get('/gyms');
        setTeamGyms(res.data || []);
      } catch (e) {
        // Non-critical: WTF cards will show '--' if gyms unavailable
        console.error('[ManagerView] Failed to fetch gyms for WTF scoping:', e);
      }
    };
    fetchGyms();
  }, []);

  // Parse WTF lead stats (all values are strings)
  const wtfLeadData = useMemo(() => {
    if (!wtfLeadStats || typeof wtfLeadStats !== 'object' || Array.isArray(wtfLeadStats)) {
      return null;
    }
    return {
      totalWalkins: parseInt(wtfLeadStats.total_walkins_today || wtfLeadStats.total_walkins || '0', 10),
      todaysLeads: parseInt(wtfLeadStats.todays_leads || '0', 10),
      convertedLeads: parseInt(wtfLeadStats.converted_leads || '0', 10),
    };
  }, [wtfLeadStats]);

  // Build per-gym target lookup from targets feed (array)
  const wtfTargetByGym = useMemo(() => {
    const lookup = {};
    if (Array.isArray(wtfTargets)) {
      wtfTargets.forEach(item => {
        if (item.gym_name) {
          lookup[item.gym_name.toLowerCase()] = {
            dailyTarget: item.daily_target || 0,
            dailyAchieved: item.daily_achieved || 0,
            remaining: item.remaining_target || 0,
          };
        }
      });
    }
    return lookup;
  }, [wtfTargets]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [dashboardRes, metricsRes] = await Promise.all([
        axiosInstance.get('/manager-dashboard', { params: { date } }),
        axiosInstance.get('/command-center', { params: { date } })
      ]);
      setDashboardData(dashboardRes.data);
      const glance = metricsRes.data?.today_at_glance || {};
      setKeyMetrics({
        visit_scheduled: glance.visit_scheduled || 0,
        visit_done: glance.visit_done || 0,
        closure_expected: glance.closure_expected || 0,
        closure_done: glance.closure_done || 0
      });
    } catch (error) {
      toast.error('Failed to load manager dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [date]);

  if (loading) {
    return (
      <div data-testid="manager-dashboard-page" className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="manager-dashboard-page" className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1F4E79] flex items-center gap-2">
            <Users size={28} />
            Manager Dashboard
          </h1>
          <p className="text-sm text-gray-500">Actionable insights for {user.name || 'Manager'}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40"
              data-testid="date-input"
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchDashboard} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Action Required */}
      <Card className="mb-6 border-amber-200 bg-amber-50" data-testid="action-required-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-amber-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Action Required
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-3xl font-bold text-amber-700">{dashboardData?.pendingVerifications || 0}</p>
              <p className="text-sm text-gray-600">Pending Verifications</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate('/verification')}>
                Review
              </Button>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-3xl font-bold text-red-600">{dashboardData?.problemGyms || 0}</p>
              <p className="text-sm text-gray-600">Gyms with Issues</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate('/command-center')}>
                View
              </Button>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <p className="text-3xl font-bold text-blue-600">{dashboardData?.unfulfilled || 0}</p>
              <p className="text-sm text-gray-600">Unfulfilled Commitments</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate('/commitment-dashboard')}>
                Follow Up
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Sales Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-blue-600">{keyMetrics.visit_scheduled}</p>
            <p className="text-xs text-gray-600">Visit Scheduled</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-green-600">{keyMetrics.visit_done}</p>
            <p className="text-xs text-gray-600">Visit Done</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-purple-600">{keyMetrics.closure_expected}</p>
            <p className="text-xs text-gray-600">Closure Expected</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-emerald-600">{keyMetrics.closure_done}</p>
            <p className="text-xs text-gray-600">Closure Done</p>
          </CardContent>
        </Card>
      </div>

      {/* WTF Live KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-2 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-purple-600 font-medium uppercase">WTF Walk-ins Today</p>
              <WtfSyncBadge lastSync={wtfSync} stale={wtfStale} size="sm" showLabel={false} />
            </div>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfLeadData?.totalWalkins ?? null} loading={wtfLoading} stale={wtfStale} />
            </p>
          </CardContent>
        </Card>
        <Card className="border-2 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-600 font-medium uppercase">WTF Leads Today</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfLeadData?.todaysLeads ?? null} loading={wtfLoading} stale={wtfStale} />
            </p>
          </CardContent>
        </Card>
        <Card className="border-2 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-600 font-medium uppercase">WTF Converted</p>
            <p className="text-2xl font-bold mt-1">
              <WtfDataColumn value={wtfLeadData?.convertedLeads ?? null} loading={wtfLoading} stale={wtfStale} />
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Team Performance */}
      <Card className="mb-6" data-testid="team-performance-table">
        <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck size={18} />
            Team Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#D6DCE5]">
                <TableHead className="font-bold">Team Leader</TableHead>
                <TableHead className="font-bold text-center">Gyms</TableHead>
                <TableHead className="font-bold text-right">Dialed</TableHead>
                <TableHead className="font-bold text-right">Connect %</TableHead>
                <TableHead className="font-bold text-right">Closures</TableHead>
                <TableHead className="font-bold text-right text-purple-600">WTF Target</TableHead>
                <TableHead className="font-bold text-right text-purple-600">WTF Achieved</TableHead>
                <TableHead className="font-bold text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dashboardData?.teamPerformance?.map((tl, idx) => (
                <TableRow key={tl.tl_id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <TableCell className="font-medium">{tl.tl_name}</TableCell>
                  <TableCell className="text-center">{tl.gymCount}</TableCell>
                  <TableCell className="text-right">{tl.dialed}</TableCell>
                  <TableCell className="text-right">
                    <span style={{
                      color: tl.connectRate >= 70 ? '#28A745' : tl.connectRate >= 60 ? '#FFC107' : '#DC3545'
                    }}>
                      {tl.connectRate}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-bold">{tl.closures}</TableCell>
                  <TableCell className="text-right">
                    {(() => {
                      // Match by gym_name from targets feed
                      const gymNames = (tl.gym_names || []).map(n => n.toLowerCase());
                      const matched = gymNames.map(n => wtfTargetByGym[n]).filter(Boolean);
                      if (matched.length === 0) return <span className="text-gray-400 text-xs">--</span>;
                      const totalTarget = matched.reduce((s, g) => s + g.dailyTarget, 0);
                      return <span className="text-purple-700 font-medium">{totalTarget}</span>;
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    {(() => {
                      const gymNames = (tl.gym_names || []).map(n => n.toLowerCase());
                      const matched = gymNames.map(n => wtfTargetByGym[n]).filter(Boolean);
                      if (matched.length === 0) return <span className="text-gray-400 text-xs">--</span>;
                      const totalAchieved = matched.reduce((s, g) => s + g.dailyAchieved, 0);
                      return <span className="text-purple-700 font-medium">{totalAchieved}</span>;
                    })()}
                  </TableCell>
                  <TableCell className="text-center">
                    {tl.hasProblem ?
                      <Badge variant="destructive" className="text-xs">Issues</Badge> :
                      <Badge variant="outline" className="text-xs">OK</Badge>
                    }
                  </TableCell>
                </TableRow>
              ))}
              {(!dashboardData?.teamPerformance || dashboardData.teamPerformance.length === 0) && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No team data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Alerts */}
      <Card data-testid="alerts-card">
        <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle size={18} />
            Recent Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 max-h-64 overflow-y-auto">
          {(!dashboardData?.alerts || dashboardData.alerts.length === 0) ? (
            <div className="p-6 text-center text-gray-500">
              <span className="text-green-600 text-2xl">&#10003;</span>
              <p className="mt-2">No recent alerts for your gyms</p>
            </div>
          ) : (
            <div className="divide-y">
              {dashboardData.alerts.slice(0, 10).map((alert, idx) => (
                <div key={idx} className={`p-3 ${alert.severity === 'critical' ? 'bg-red-50' : 'bg-yellow-50'}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{alert.severity === 'critical' ? '\u{1F534}' : '\u{1F7E1}'}</span>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{alert.gym_name}</div>
                      <div className="text-xs text-gray-600">{alert.issue}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
